import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  Fade,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MyButton } from '../index';
import { hasuraClient } from '../../utils/graphqlClient';
import { RESET_PASSWORD_MUTATION } from '../../graphql/mutations/auth';
import { useFormValidation, validationSchemas } from '../../utils/validation';
import { resetPasswordModalStyles } from './ResetPasswordModal.styles';
import { useAuth } from '../../hooks';

const ResetPasswordModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    data: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    reset,
  } = useFormValidation(
    { oldPassword: '', newPassword: '', confirmPassword: '' },
    {
      oldPassword: validationSchemas.resetPassword.oldPassword,
      newPassword: validationSchemas.resetPassword.newPassword,
      confirmPassword: validationSchemas.resetPassword.confirmPassword,
    }
  );

  const handleInputChange = useCallback(
    field => event => {
      const value = event.target.value;
      handleChange(field, value);
      if (submitError) {
        setSubmitError(null);
      }
      // Re-validate confirmPassword when newPassword changes
      if (field === 'newPassword' && touched.confirmPassword) {
        handleBlur('confirmPassword');
      }
    },
    [handleChange, submitError, touched, handleBlur]
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      const { isValid: formIsValid } = validateAll();
      if (!formIsValid) {
        return;
      }

      // Check if passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        setSubmitError(
          t('auth.resetPassword.errors.passwordsNotMatch', 'Yeni şifreler eşleşmiyor.')
        );
        return;
      }

      try {
        setIsSubmitting(true);
        setSubmitError(null);

        if (!user?.email) {
          setSubmitError(
            t('auth.resetPassword.errors.userNotFound', 'Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.')
          );
          setIsSubmitting(false);
          return;
        }

        const response = await hasuraClient.mutate(
          RESET_PASSWORD_MUTATION,
          {
            input: {
              oldPassword: formData.oldPassword,
              newPassword: formData.newPassword,
              email: user.email,
            },
          }
        );

        if (response?.resetPassword?.success) {
          setSubmitSuccess(true);
          reset();
        } else {
          setSubmitError(
            response?.resetPassword?.message ||
              t('auth.resetPassword.errors.submitFailed', 'Şifre sıfırlama başarısız oldu.')
          );
        }
      } catch (error) {
        setSubmitError(
          error.message ||
            t('auth.resetPassword.errors.submitError', 'Bir hata oluştu. Lütfen tekrar deneyin.')
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateAll, reset, t, user]
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      reset();
      setSubmitSuccess(false);
      setSubmitError(null);
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      onClose();
    }
  }, [isSubmitting, reset, onClose]);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={resetPasswordModalStyles.dialog(theme)}
      PaperProps={{
        sx: resetPasswordModalStyles.paper(theme),
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <DialogTitle sx={resetPasswordModalStyles.header(theme)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={resetPasswordModalStyles.iconContainer(theme)}>
                {submitSuccess ? (
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
                ) : (
                  <LockIcon sx={{ fontSize: 32, color: 'white' }} />
                )}
              </Box>
              <Typography variant="h5" sx={resetPasswordModalStyles.title(theme)}>
                {submitSuccess
                  ? t('auth.resetPassword.success.title', 'Şifre Güncellendi!')
                  : t('auth.resetPassword.title', 'Şifre Sıfırla')
                }
              </Typography>
              {!submitSuccess && (
                <Typography variant="body2" sx={resetPasswordModalStyles.description}>
                  {t(
                    'auth.resetPassword.description',
                    'Eski şifrenizi ve yeni şifrenizi girin.'
                  )}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={isSubmitting}
            sx={{
              color: 'text.secondary',
              position: 'absolute',
              top: theme.spacing(2),
              right: theme.spacing(2),
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={resetPasswordModalStyles.content(theme)}>
        {submitSuccess ? (
          <Fade in timeout={400}>
            <Box sx={resetPasswordModalStyles.successContainer(theme)}>
              <Box sx={resetPasswordModalStyles.successIconContainer(theme)}>
                <CheckCircleIcon
                  sx={{
                    fontSize: 48,
                    color: 'success.main',
                  }}
                />
              </Box>
              <Typography variant="h6" sx={resetPasswordModalStyles.successTitle(theme)}>
                {t('auth.resetPassword.success.title', 'Şifre Güncellendi!')}
              </Typography>
              <Typography variant="body1" sx={resetPasswordModalStyles.successMessage(theme)}>
                {t(
                  'auth.resetPassword.success.message',
                  'Şifreniz başarıyla güncellendi. Yeni şifrenizle giriş yapabilirsiniz.'
                )}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Fade in timeout={400}>
            <Box>
              {submitError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                  }}
                  onClose={() => setSubmitError(null)}
                >
                  {submitError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate sx={resetPasswordModalStyles.formContainer(theme)}>
                <TextField
                  fullWidth
                  label={t('auth.resetPassword.fields.oldPassword', 'Eski Şifre')}
                  type={showOldPassword ? 'text' : 'password'}
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleInputChange('oldPassword')}
                  onBlur={() => handleBlur('oldPassword')}
                  error={touched.oldPassword && !!errors.oldPassword}
                  helperText={touched.oldPassword && errors.oldPassword}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                  required
                  sx={resetPasswordModalStyles.textField(theme)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{
                            color: touched.oldPassword && errors.oldPassword
                              ? 'error.main'
                              : 'action.disabled',
                            fontSize: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          edge="end"
                          size="small"
                        >
                          {showOldPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label={t('auth.resetPassword.fields.newPassword', 'Yeni Şifre')}
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange('newPassword')}
                  onBlur={() => handleBlur('newPassword')}
                  error={touched.newPassword && !!errors.newPassword}
                  helperText={touched.newPassword && errors.newPassword}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                  sx={resetPasswordModalStyles.textField(theme)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{
                            color: touched.newPassword && errors.newPassword
                              ? 'error.main'
                              : 'action.disabled',
                            fontSize: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label={t('auth.resetPassword.fields.confirmPassword', 'Yeni Şifre (Tekrar)')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  onBlur={() => handleBlur('confirmPassword')}
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                  sx={resetPasswordModalStyles.textField(theme)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{
                            color: touched.confirmPassword && errors.confirmPassword
                              ? 'error.main'
                              : 'action.disabled',
                            fontSize: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={resetPasswordModalStyles.actions(theme)}>
        {submitSuccess ? (
          <MyButton
            variant="contained"
            onClick={handleClose}
            fullWidth
            sx={resetPasswordModalStyles.submitButton(theme)}
          >
            {t('common.close')}
          </MyButton>
        ) : (
          <>
            <MyButton
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={resetPasswordModalStyles.button(theme)}
            >
              {t('common.cancel')}
            </MyButton>
            <MyButton
              variant="contained"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !formData.oldPassword ||
                !formData.newPassword ||
                !formData.confirmPassword ||
                !!errors.oldPassword ||
                !!errors.newPassword ||
                !!errors.confirmPassword
              }
              sx={resetPasswordModalStyles.submitButton(theme)}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                  {t('auth.resetPassword.updating', 'Güncelleniyor...')}
                </>
              ) : (
                t('auth.resetPassword.update', 'Güncelle')
              )}
            </MyButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordModal;

